import { EventTypesEnum } from 'types/generated/client';
import { isPackEvent } from 'utils/shared/time/getEventSchedule';

export type EventKind = 'tournament' | 'league' | 'openPlay' | 'social' | 'rfx' | 'general';

export interface EventExperience {
  kind: EventKind;
  /** "Tournament": the type as a title or badge. Empty for the GENERAL_EVENT catch-all. */
  label: string;
  /** "tournament": the noun inside a sentence ("Withdraw from tournament"). */
  noun: string;
  nounPlural: string;
  /** "Compete in" or "Join". */
  joinVerb: string;
  /** Gameday, brackets, score upload, the format chip and division details. */
  hasCompetition: boolean;
  /** Doubles partner selection and invites during registration. */
  hasPartners: boolean;
  /** One group with several sessions, paid once. */
  isPack: boolean;
  /** Title of the date card: "Schedule" for a pack, else the type's date. */
  dateCardTitle: string;
}

interface EventExperienceSource {
  /** Some register forms select the event loosely; a missing type is the catch-all. */
  type?: EventTypesEnum | null;
  isRfx?: boolean | null;
  groups?: { timeSlots?: { id: string }[] | null }[] | null;
}

type Shape = Omit<EventExperience, 'isPack' | 'dateCardTitle'>;

const COMPETITIVE = { hasCompetition: true, hasPartners: true, joinVerb: 'Compete in' };
const CASUAL = { hasCompetition: false, hasPartners: false, joinVerb: 'Join' };

const SHAPES: Record<EventKind, Shape> = {
  tournament: {
    kind: 'tournament',
    label: 'Tournament',
    noun: 'tournament',
    nounPlural: 'tournaments',
    ...COMPETITIVE,
  },
  league: {
    kind: 'league',
    label: 'League',
    noun: 'league',
    nounPlural: 'leagues',
    ...COMPETITIVE,
  },
  // GENERAL_EVENT is the catch-all (external round robins, imports), so it keeps the competitive page.
  general: { kind: 'general', label: '', noun: 'event', nounPlural: 'events', ...COMPETITIVE },
  openPlay: {
    kind: 'openPlay',
    label: 'Open Play',
    noun: 'open play session',
    nounPlural: 'open play sessions',
    ...CASUAL,
  },
  social: { kind: 'social', label: 'Social', noun: 'social', nounPlural: 'socials', ...CASUAL },
  rfx: { kind: 'rfx', label: 'RFX', noun: 'class', nounPlural: 'classes', ...CASUAL },
};

const KIND_BY_TYPE: Record<EventTypesEnum, EventKind> = {
  [EventTypesEnum.Tournament]: 'tournament',
  [EventTypesEnum.League]: 'league',
  [EventTypesEnum.OpenPlay]: 'openPlay',
  [EventTypesEnum.Social]: 'social',
  [EventTypesEnum.GeneralEvent]: 'general',
};

/**
 * What the event page shows and how registration runs for an event, decided once
 * from its type and flags. An RFX class is a GENERAL_EVENT row flagged `is_rfx`.
 */
export const getEventExperience = (event: EventExperienceSource): EventExperience => {
  const kind: EventKind = event.isRfx
    ? 'rfx'
    : (event.type && KIND_BY_TYPE[event.type]) || 'general';
  const shape = SHAPES[kind];
  const isPack = isPackEvent(event);
  return {
    ...shape,
    isPack,
    dateCardTitle: isPack
      ? 'Schedule'
      : shape.hasCompetition && shape.label
      ? `${shape.label} Date`
      : 'Event Date',
  };
};
